
import { useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info } from "lucide-react";

// Define TypeScript interfaces for template variables
interface EmailTemplateVariables {
  [key: string]: string | number | boolean | null | undefined;
}

// Email template data - these should match the actual templates in edge functions
const emailTemplates = [
  {
    id: "order-confirmation",
    name: "Order Confirmation",
    description: "Sent to customers after they place an order",
    trigger: "Order creation",
    from: "send-order-confirmation",
    fromAddress: "orders@cooksy.com",
    subject: "Your Order Confirmation - Cooksy",
    lastUpdated: "2024-03-15",
    variables: {
      customer_name: "John Doe",
      order_id: "ORD-12345",
      order_date: "July 10, 2024",
      delivery_type: "Pickup",
      scheduled_date: "July 15, 2024",
      scheduled_time: "6:00 PM - 7:00 PM",
      meal_name: "Spaghetti Bolognese",
      quantity: "2",
      meal_price: "$12.99",
      delivery_fee: "$3.99",
      order_total: "$29.97",
      chef_name: "Chef Mario",
      special_instructions: "Extra sauce on the side, please."
    } as EmailTemplateVariables,
    preview: `
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; 
              line-height: 1.5; 
              color: #333; 
              margin: 0; 
              padding: 0;
            }
            .container { 
              max-width: 600px; 
              margin: 0 auto; 
              padding: 20px; 
            }
            .header { 
              background-color: #f8f9fa; 
              padding: 20px; 
              text-align: center; 
              border-radius: 5px 5px 0 0; 
            }
            .content { 
              background-color: #fff; 
              padding: 20px; 
              border-radius: 0 0 5px 5px; 
            }
            .footer { 
              margin-top: 20px; 
              text-align: center; 
              font-size: 12px; 
              color: #6c757d; 
            }
            .button { 
              display: inline-block; 
              padding: 10px 20px; 
              background-color: #0a66c2; 
              color: white !important; 
              text-decoration: none; 
              border-radius: 5px; 
              font-weight: bold;
            }
            .order-details { 
              margin: 20px 0; 
              padding: 15px; 
              background-color: #f8f9fa; 
              border-radius: 5px; 
            }
            .divider { 
              border-top: 1px solid #e9ecef; 
              margin: 15px 0; 
            }
            .text-center { 
              text-align: center; 
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 15px 0; 
            }
            th { 
              text-align: left; 
              padding: 8px; 
              border-bottom: 1px solid #e9ecef; 
              background-color: #f8f9fa; 
            }
            td { 
              padding: 8px; 
              border-bottom: 1px solid #e9ecef; 
            }
            .total-row td { 
              font-weight: bold; 
              border-top: 2px solid #dee2e6; 
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Order Confirmation</h1>
            </div>
            <div class="content">
              <p>Hello \${customer_name},</p>
              <p>Thank you for your order! We've received your order and are working on it now.</p>
              
              <div class="order-details">
                <h2>Order Summary</h2>
                <p><strong>Order #:</strong> \${order_id}</p>
                <p><strong>Order Date:</strong> \${order_date}</p>
                <p><strong>Delivery Method:</strong> \${delivery_type}</p>
                <p><strong>Scheduled for:</strong> \${scheduled_date} at \${scheduled_time}</p>
                
                <table>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Quantity</th>
                      <th style="text-align: right;">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>\${meal_name}</td>
                      <td>\${quantity}</td>
                      <td style="text-align: right;">\${meal_price}</td>
                    </tr>
                    <tr>
                      <td colspan="2">Delivery Fee</td>
                      <td style="text-align: right;">\${delivery_fee}</td>
                    </tr>
                    <tr class="total-row">
                      <td colspan="2">Total</td>
                      <td style="text-align: right;">\${order_total}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div class="divider"></div>
              
              <p><strong>Chef:</strong> \${chef_name}</p>
              
              {special_instructions && (
                <div>
                  <p><strong>Special Instructions:</strong></p>
                  <p>\${special_instructions}</p>
                </div>
              )}
              
              <div class="text-center" style="margin: 30px 0;">
                <a href="https://cooksymeals.com/order/\${order_id}" class="button">
                  View Order
                </a>
              </div>
              
              <p>If you have any questions about your order, please contact us or the chef directly through the Cooksy platform.</p>
            </div>
            
            <div class="footer">
              <p>© 2023 Cooksy. All rights reserved.</p>
              <p>If you have any questions, please contact us at support@cooksymeals.com</p>
            </div>
          </div>
        </body>
      </html>
    `
  },
  {
    id: "order-ready",
    name: "Order Ready",
    description: "Sent to customers when their order is ready for pickup",
    trigger: "Order status change to 'ready'",
    from: "send-order-confirmation",
    fromAddress: "orders@cooksy.com",
    subject: "Your Order is Ready for Pickup - Cooksy",
    lastUpdated: "2024-03-15",
    variables: {
      customer_name: "John Doe",
      order_id: "ORD-12345",
      order_date: "July 10, 2024",
      meal_name: "Spaghetti Bolognese",
      quantity: "2",
      chef_name: "Chef Mario",
      pickup_time: "6:00 PM - 7:00 PM",
      pickup_address: "123 Main St, San Francisco, CA",
      pickup_instructions: "Come to the side entrance and ring bell #3",
      special_instructions: "Extra sauce on the side, please."
    } as EmailTemplateVariables,
    preview: `
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; 
              line-height: 1.5; 
              color: #333; 
              margin: 0; 
              padding: 0;
            }
            .container { 
              max-width: 600px; 
              margin: 0 auto; 
              padding: 20px; 
            }
            .header { 
              background-color: #f8f9fa; 
              padding: 20px; 
              text-align: center; 
              border-radius: 5px 5px 0 0; 
            }
            .content { 
              background-color: #fff; 
              padding: 20px; 
              border-radius: 0 0 5px 5px; 
            }
            .footer { 
              margin-top: 20px; 
              text-align: center; 
              font-size: 12px; 
              color: #6c757d; 
            }
            .button { 
              display: inline-block; 
              padding: 10px 20px; 
              background-color: #0a66c2; 
              color: white !important; 
              text-decoration: none; 
              border-radius: 5px; 
              font-weight: bold;
            }
            .order-details { 
              margin: 20px 0; 
              padding: 15px; 
              background-color: #f8f9fa; 
              border-radius: 5px; 
            }
            .highlight-box { 
              background-color: #e8f5e9; 
              padding: 15px; 
              border-radius: 5px; 
              margin: 15px 0; 
              border-left: 4px solid #2d8a1e; 
            }
            .divider { 
              border-top: 1px solid #e9ecef; 
              margin: 15px 0; 
            }
            .text-center { 
              text-align: center; 
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Your Order is Ready for Pickup!</h1>
            </div>
            <div class="content">
              <p>Hello \${customer_name},</p>
              <p>Great news! Your order for <strong>\${quantity}x \${meal_name}</strong> from Chef \${chef_name} is now ready for pickup.</p>
              
              <div class="highlight-box">
                <h3>Pickup Details</h3>
                <p><strong>Pickup Window:</strong> \${pickup_time}</p>
                <p><strong>Pickup Address:</strong> \${pickup_address}</p>
                
                {pickup_instructions && (
                  <div>
                    <p><strong>Pickup Instructions:</strong></p>
                    <p>\${pickup_instructions}</p>
                  </div>
                )}
              </div>
              
              <div class="order-details">
                <h3>Order Summary</h3>
                <p><strong>Order ID:</strong> \${order_id}</p>
                <p><strong>Order Date:</strong> \${order_date}</p>
                <p><strong>Item:</strong> \${quantity}x \${meal_name}</p>
                
                {special_instructions && (
                  <p><strong>Special Instructions:</strong> \${special_instructions}</p>
                )}
              </div>
              
              <div class="divider"></div>
              
              <p>Please remember to bring your ID for verification if this is your first order with the chef.</p>
              
              <div class="text-center">
                <a href="https://cooksymeals.com/order/\${order_id}" class="button">
                  View Order
                </a>
              </div>
            </div>
            
            <div class="footer">
              <p>© 2023 Cooksy. All rights reserved.</p>
              <p>If you have any questions, please contact us at support@cooksymeals.com</p>
            </div>
          </div>
        </body>
      </html>
    `
  },
  {
    id: "order-status",
    name: "Order Status Update",
    description: "Sent to customers when their order status changes",
    trigger: "Order status change",
    from: "send-order-confirmation",
    fromAddress: "updates@cooksy.com",
    subject: "Order Status Update - Cooksy",
    lastUpdated: "2024-03-15",
    variables: {
      customer_name: "John Doe",
      status_message: "Your order is on the way!",
      order_id: "ORD-12345",
      order_date: "July 10, 2024",
      delivery_type: "Delivery",
      meal_name: "Spaghetti Bolognese",
      quantity: "2",
      chef_name: "Chef Mario",
      scheduled_date: "July 15, 2024",
      scheduled_time: "6:00 PM - 7:00 PM",
      delivery_address: "123 Main St, San Francisco, CA",
      special_instructions: "Leave at door, please."
    } as EmailTemplateVariables,
    preview: `
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; 
              line-height: 1.5; 
              color: #333; 
              margin: 0; 
              padding: 0;
            }
            .container { 
              max-width: 600px; 
              margin: 0 auto; 
              padding: 20px; 
            }
            .header { 
              background-color: #f8f9fa; 
              padding: 20px; 
              text-align: center; 
              border-radius: 5px 5px 0 0; 
            }
            .content { 
              background-color: #fff; 
              padding: 20px; 
              border-radius: 0 0 5px 5px; 
            }
            .footer { 
              margin-top: 20px; 
              text-align: center; 
              font-size: 12px; 
              color: #6c757d; 
            }
            .button { 
              display: inline-block; 
              padding: 10px 20px; 
              background-color: #0a66c2; 
              color: white !important; 
              text-decoration: none; 
              border-radius: 5px; 
              font-weight: bold;
            }
            .order-details { 
              margin: 20px 0; 
              padding: 15px; 
              background-color: #f8f9fa; 
              border-radius: 5px; 
            }
            .divider { 
              border-top: 1px solid #e9ecef; 
              margin: 15px 0; 
            }
            .text-center { 
              text-align: center; 
            }
            .status-highlight { 
              font-size: 18px; 
              color: #0a66c2; 
              font-weight: bold; 
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Order Status Update</h1>
            </div>
            <div class="content">
              <p>Hello \${customer_name},</p>
              <p class="status-highlight">\${status_message}</p>
              <p>
                Your order for <strong>\${quantity}x \${meal_name}</strong> from Chef \${chef_name} has been updated.
              </p>
              
              <div class="order-details">
                <h3>Order Details</h3>
                <p><strong>Order ID:</strong> \${order_id}</p>
                <p><strong>Order Date:</strong> \${order_date}</p>
                <p>
                  <strong>Delivery Method:</strong> \${delivery_type}
                </p>
                {delivery_address && (
                  <p><strong>Delivery Address:</strong> \${delivery_address}</p>
                )}
                <p>
                  <strong>\${delivery_type === 'pickup' ? 'Pickup' : 'Delivery'} Date:</strong> \${scheduled_date}
                </p>
                <p>
                  <strong>\${delivery_type === 'pickup' ? 'Pickup' : 'Delivery'} Time:</strong> \${scheduled_time}
                </p>
                {special_instructions && (
                  <p><strong>Special Instructions:</strong> \${special_instructions}</p>
                )}
              </div>
              
              <div class="divider"></div>
              
              <div class="text-center">
                <a href="https://cooksymeals.com/order/\${order_id}" class="button">
                  View Order
                </a>
              </div>
            </div>
            
            <div class="footer">
              <p>© 2023 Cooksy. All rights reserved.</p>
              <p>If you have any questions, please contact us at support@cooksymeals.com</p>
            </div>
          </div>
        </body>
      </html>
    `
  },
  {
    id: "new-order",
    name: "New Order Notification",
    description: "Sent to chefs when they receive a new order",
    trigger: "Order creation",
    from: "send-chef-notifications",
    fromAddress: "orders@cooksy.com",
    subject: "New Order Received - Cooksy",
    lastUpdated: "2024-03-15",
    variables: {
      meal_name: "Spaghetti Bolognese",
      order_id: "ORD-12345",
      customer_name: "John Doe",
      order_total: "$29.97",
      quantity: "2",
      delivery_type: "Delivery",
      delivery_address: "123 Main St, San Francisco, CA",
      special_instructions: "Leave at door, please."
    } as EmailTemplateVariables,
    preview: `
      <div style="font-family: system-ui, sans-serif; padding: 24px;">
        <h1 style="color: #333; margin-bottom: 24px;">New Order Received!</h1>
        <p>You have received a new order for \${meal_name}.</p>
        <div style="background: #f9f9f9; padding: 16px; border-radius: 8px; margin: 24px 0;">
          <h2 style="color: #333; margin-bottom: 16px;">Order Details:</h2>
          <p><strong>Order ID:</strong> \${order_id}</p>
          <p><strong>Customer:</strong> \${customer_name}</p>
          <p><strong>Amount:</strong> \${order_total}</p>
          <p><strong>Quantity:</strong> \${quantity}</p>
          <p><strong>Delivery Type:</strong> \${delivery_type}</p>
          \${delivery_address ? 
            \`<p><strong>Delivery Address:</strong> \${delivery_address}</p>\` : 
            ''
          }
          \${special_instructions ? 
            \`<p><strong>Special Instructions:</strong> \${special_instructions}</p>\` : 
            ''
          }
        </div>
        <p>Please log in to the Cooksy platform to manage this order.</p>
        <div style="text-align: center; margin-top: 30px;">
          <a href="https://cooksymeals.com/seller/orders" 
             style="background-color: #0a66c2; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; font-weight: bold;">
            View in Dashboard
          </a>
        </div>
      </div>
    `
  },
  {
    id: "verification-status",
    name: "Verification Status Update",
    description: "Sent to chefs when their verification status changes",
    trigger: "Verification status change",
    from: "send-chef-notifications",
    fromAddress: "verification@cooksy.com",
    subject: "Chef Verification Status Update - Cooksy",
    lastUpdated: "2024-03-15",
    variables: {
      chef_name: "Mario",
      verification_status: "approved",
      verification_feedback: "Your application looks great! Welcome to Cooksy."
    } as EmailTemplateVariables,
    preview: `
      <div style="font-family: system-ui, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e2e8f0; border-radius: 8px;">
        <h1 style="color: #0f172a; font-size: 24px; margin-bottom: 16px;">Verification Status Update</h1>
        <p style="color: #334155; font-size: 16px; margin-bottom: 24px;">Hello Chef \${chef_name},</p>
        <p style="color: #334155; font-size: 16px; margin-bottom: 24px;">Your verification status has been updated to: <strong>\${verification_status}</strong></p>
        
        <div style="background: #f8fafc; border-radius: 6px; padding: 16px; margin-bottom: 24px;">
          <h2 style="color: #0f172a; font-size: 18px; margin-bottom: 12px;">What This Means</h2>
          {verification_status === 'approved' ? (
            <p style="color: #334155; margin-bottom: 8px;">Congratulations! You've been approved as a chef on Cooksy. You can now start creating and selling meals on our platform.</p>
          ) : verification_status === 'rejected' ? (
            <p style="color: #334155; margin-bottom: 8px;">Unfortunately, your verification was not approved at this time. Please review the feedback below and you may reapply after making the necessary adjustments.</p>
          ) : (
            <p style="color: #334155; margin-bottom: 8px;">Your application is under review. We'll notify you once a decision has been made.</p>
          )}
        </div>
        
        {verification_feedback && (
          <div style="background: #fff7ed; border-radius: 6px; padding: 16px; margin-bottom: 24px; border-left: 4px solid #f97316;">
            <h2 style="color: #0f172a; font-size: 18px; margin-bottom: 12px;">Feedback</h2>
            <p style="color: #334155; margin-bottom: 8px;">\${verification_feedback}</p>
          </div>
        )}
        
        <div style="text-align: center; margin-top: 30px;">
          <a href="https://cooksymeals.com/seller/profile" 
             style="background-color: #0a66c2; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; font-weight: bold;">
            Go to My Profile
          </a>
        </div>
        
        <p style="color: #334155; margin-bottom: 8px; margin-top: 30px;">Thank you for being part of the Cooksy community!</p>
      </div>
    `
  }
];

const EmailTemplates = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(emailTemplates[0]);

  const formatTriggerName = (trigger: string) => {
    return trigger.split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  };

  // Extract template variables from the preview HTML
  const extractVariables = (html: string) => {
    const regex = /\{([^}]+)\}/g;
    const matches = html.match(regex);
    if (!matches) return [];
    
    // Remove duplicates and sort
    return [...new Set(matches)].sort();
  };

  return (
    <AdminLayout title="Email Templates">
      <div className="max-w-7xl mx-auto pb-12">
        <Alert className="mb-6">
          <Info className="h-4 w-4" />
          <AlertTitle>Important note about email templates</AlertTitle>
          <AlertDescription>
            These previews represent the actual email templates used by the system. The templates are implemented as React components in Edge Functions and synchronized with this view.
            Last template sync: {selectedTemplate.lastUpdated}
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-xl">Email Templates</CardTitle>
                <CardDescription>
                  These templates are used for system notifications and are stored in Edge Functions.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {emailTemplates.map((template) => (
                    <div 
                      key={template.id}
                      className={`p-4 rounded-md hover:bg-slate-100 cursor-pointer transition-colors ${selectedTemplate.id === template.id ? 'bg-slate-100 border-l-4 border-primary' : 'border-l-4 border-transparent'}`}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-base">{template.name}</h3>
                        <Badge variant="outline" className="ml-2">{template.from}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{template.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div>
                    <CardTitle className="text-xl mb-1">{selectedTemplate.name}</CardTitle>
                    <CardDescription>
                      <span className="block mb-1">
                        <span className="font-medium">Trigger:</span> {formatTriggerName(selectedTemplate.trigger)}
                      </span>
                      <span className="block mb-1">
                        <span className="font-medium">Function:</span> {selectedTemplate.from}
                      </span>
                      <span className="block mb-1">
                        <span className="font-medium">From Address:</span> {selectedTemplate.fromAddress}
                      </span>
                      <span className="block">
                        <span className="font-medium">Subject:</span> {selectedTemplate.subject}
                      </span>
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-300">Template</Badge>
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-200 border-green-300">{formatTriggerName(selectedTemplate.trigger)}</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="preview" className="w-full">
                  <TabsList className="mb-4 w-full justify-start">
                    <TabsTrigger value="preview" className="flex-1 md:flex-none md:px-6">Preview</TabsTrigger>
                    <TabsTrigger value="html" className="flex-1 md:flex-none md:px-6">HTML Source</TabsTrigger>
                    <TabsTrigger value="variables" className="flex-1 md:flex-none md:px-6">Variables</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="preview">
                    <div className="border rounded-lg p-6 bg-white overflow-auto" style={{height: '480px'}}>
                      <div dangerouslySetInnerHTML={{ __html: selectedTemplate.preview }} />
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="html">
                    <ScrollArea className="border rounded-lg p-6 bg-slate-50 overflow-auto" style={{height: '480px'}}>
                      <pre className="text-xs text-slate-800 whitespace-pre-wrap font-mono">
                        {selectedTemplate.preview}
                      </pre>
                    </ScrollArea>
                  </TabsContent>
                  
                  <TabsContent value="variables">
                    <div className="border rounded-lg p-6 bg-white" style={{minHeight: '200px'}}>
                      <h3 className="text-base font-semibold mb-4">Template Variables</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {extractVariables(selectedTemplate.preview).map((variable, index) => (
                          <div key={index} className="bg-slate-50 rounded-md p-3 border border-slate-200">
                            <Badge variant="secondary" className="justify-start mb-1 font-mono">
                              {variable}
                            </Badge>
                            <p className="text-xs text-slate-500">Data inserted at runtime</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
                
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-blue-800 text-sm">
                    <strong>Note:</strong> These are preview representations of the templates used by the system. The actual templates are implemented as React components in Edge Functions.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default EmailTemplates;
